import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TITLE = "WhatsApp Alerts for Journalists — X Monitoring | WallaWhats";
const DESCRIPTION = 'Track sources, rivals, and breaking news on X with instant WhatsApp alerts. Built for reporters who can\'t afford to miss a tweet.';
const CANONICAL = 'https://wallawhats.com/for-journalists';

export function ForJournalistsPage() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.title = TITLE;
    document.querySelector('meta[name="description"]')?.setAttribute('content', DESCRIPTION);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', CANONICAL);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', TITLE);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', DESCRIPTION);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', CANONICAL);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <Problem />
        <UseCases />
        <SampleAlert />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
        <Link to="/">
          <img src="/assets/logo/logo-full-dark-text.png" alt="WallaWhats" className="h-9" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900">Pricing</Link>
          <Link to="/signin" className="text-sm text-gray-600 hover:text-gray-900">Sign in</Link>
          <Link to="/signup" className="rounded-full bg-primary-600 px-5 py-2 text-xs font-semibold text-white hover:bg-primary-700">
            Start free
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-4xl px-6 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">For journalists & news desks</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
        Real-time X monitoring,<br className="hidden sm:block" /> delivered to WhatsApp
      </h1>
      <p className="mt-5 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
        The X algorithm doesn't care about your deadline. WallaWhats watches the accounts you actually need —
        sources, rivals, government desks, breaking-news handles — and pings WhatsApp the second they post.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/signup" className="rounded-full bg-primary-600 px-8 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
          Start free — 30-second setup
        </Link>
        <Link to="/pricing" className="rounded-full border border-gray-300 px-8 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
          See plans
        </Link>
      </div>
      <p className="mt-4 text-xs text-gray-400">No credit card. 2 monitored accounts free, forever.</p>
    </section>
  );
}

function Problem() {
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
          The algorithm problem for newsrooms
        </h2>
        <p className="mt-4 text-base text-gray-600 text-center max-w-2xl mx-auto">
          X's feed optimizes for engagement, not urgency. A breaking-news tweet from a government official can
          sit hidden behind viral memes. Every newsroom that depends on Twitter has felt this.
        </p>
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {[
            { t: 'Timing delays', d: 'Important posts can take 30 minutes to surface in your feed — if they ever appear at all.' },
            { t: 'Algorithmic ranking', d: 'Engagement-bait threads outrank substantive scoops. The algorithm has no concept of "newsworthy".' },
            { t: 'Notification fatigue', d: 'Turn on notifications for everyone, get drowned in noise. Turn them off, miss the one that mattered.' },
            { t: 'Single-platform fragility', d: 'Open the X app to check. Forget. Miss the source\'s tweet. Your editor finds out from a competitor.' },
          ].map(({ t, d }) => (
            <li key={t} className="rounded-xl bg-white p-6 border border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">{t}</h3>
              <p className="mt-2 text-sm text-gray-600">{d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function UseCases() {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
          What journalists actually monitor
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          <UseCaseCard
            icon="📡"
            title="Source tracking"
            desc="Follow your beat's primary sources — government officials, executives, lead investigators, regulators. The accounts whose first post on a story IS the story."
          />
          <UseCaseCard
            icon="📰"
            title="Rival desk monitoring"
            desc="Track competitor outlets and reporters. When their bureau breaks something, you know in seconds, not from a Slack message at 3pm."
          />
          <UseCaseCard
            icon="🚨"
            title="Breaking-news handles"
            desc="Wire services, official press accounts, emergency services. Pre-curate the handles whose posts always demand attention."
          />
          <UseCaseCard
            icon="🌍"
            title="Foreign-language sources"
            desc="Cover stories where the original primary source posts in Russian, Arabic, Mandarin. Get alerts; translate on your timeline, not theirs."
          />
          <UseCaseCard
            icon="🔍"
            title="Investigative leads"
            desc="OSINT accounts, leaks aggregators, data-broker postings. The first hour of a leak's life is when most of the value lives."
          />
          <UseCaseCard
            icon="📊"
            title="Market-moving voices"
            desc="Central bank governors, finance-ministers, activist investors. For business desks, these are the on-the-record ledger of policy change."
          />
        </div>
        <p className="mt-8 text-sm text-gray-500 text-center max-w-2xl mx-auto">
          Not a newsroom? <Link to="/for-marketers" className="text-primary-600 hover:text-primary-700 underline">Marketers tracking competitor accounts</Link> use
          the same setup to watch brand mentions and rival launches.
        </p>
      </div>
    </section>
  );
}

function UseCaseCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors">
      <div className="text-3xl">{icon}</div>
      <h3 className="mt-4 text-base font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function SampleAlert() {
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
          What an alert looks like
        </h2>
        <p className="mt-4 text-base text-gray-600 text-center">
          A WhatsApp message, ~6 seconds after the post goes live on X.
        </p>
        <div className="mt-10 mx-auto max-w-md rounded-2xl bg-[#075e54] p-4 shadow-xl">
          <div className="rounded-xl bg-white p-4 text-sm text-gray-800">
            <div className="font-semibold text-[#075e54]">WallaWhats</div>
            <div className="mt-1 text-xs text-gray-500">@reuters · just now</div>
            <p className="mt-2 leading-relaxed">
              BREAKING: U.S. Federal Reserve cuts benchmark rate by 25 basis points,
              first move in 11 months. Statement cites moderating inflation outlook.
            </p>
            <a href="#" className="mt-2 block text-xs text-blue-600">View on X →</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Plans for newsrooms</h2>
        <p className="mt-4 text-base text-gray-600 max-w-2xl mx-auto">
          Most desk reporters fit on Pro+ ($12/mo, 10 monitored accounts).
          Investigative teams and international bureaus typically run on Business
          ($29/mo, 20 accounts) or Enterprise ($99/mo, 50 accounts).
        </p>
        <Link
          to="/pricing"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
        >
          Compare plans
        </Link>
      </div>
    </section>
  );
}

function FAQ() {
  const items: { q: string; a: React.ReactNode }[] = [
    { q: 'How fast are alerts?', a: '6-8 seconds end-to-end from X-post-published to WhatsApp-delivered. We use X\'s filtered stream API and the WhatsApp Cloud API — no polling, no batching.' },
    { q: 'Can I monitor protected/private accounts?', a: 'No — only public X posts. WallaWhats uses X\'s public stream API, which respects the visibility settings of the account being tracked.' },
    { q: 'What about retweets and replies?', a: 'Original posts only by default. Retweets and replies generate noise without adding sourceable signal. (You can change this in account settings if you specifically need reply-tracking.)' },
    { q: 'Is there a daily digest option?', a: 'Yes. You can configure each tracked account to deliver instantly OR roll up into an hourly digest. Newsrooms typically run tier-1 sources on instant and a wider tier-2 watch list on digest.' },
    { q: 'Can the whole desk receive the same alerts?', a: 'Yes. One WallaWhats account can route alerts to multiple WhatsApp numbers. Use it for a desk-wide alert channel — all reporters see the same scoop at the same moment.' },
    {
      q: 'Is this a replacement for TweetDeck?',
      a: (
        <>
          For newsroom monitoring, yes — WallaWhats works well as a{' '}
          <Link to="/blog/tweetdeck-alternative-whatsapp" className="text-primary-600 hover:text-primary-700 underline">
            free TweetDeck alternative
          </Link>
          : instead of columns you have to keep open and watch, tracked accounts push straight to WhatsApp the moment they post.
        </>
      ),
    },
  ];
  return (
    <section className="bg-gray-50 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">FAQ</h2>
        <dl className="mt-10 space-y-6">
          {items.map(({ q, a }) => (
            <div key={q}>
              <dt className="text-base font-semibold text-gray-900">{q}</dt>
              <dd className="mt-2 text-sm text-gray-600 leading-relaxed">{a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Set it up in 30 seconds.
        </h2>
        <p className="mt-3 text-base text-gray-600">
          No app to install. No X subscription. The next time a tracked source posts, you'll know.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="rounded-full bg-primary-600 px-8 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors">
            Start free
          </Link>
          <Link to="/blog/real-time-x-monitoring-journalists" className="rounded-full border border-gray-300 px-8 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
            Read the deep-dive
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-gray-50 py-10">
      <div className="mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between px-6 gap-4">
        <p className="text-xs text-gray-500">© 2026 WallaWhats by BWS. All rights reserved.</p>
        <div className="flex gap-5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-900">Home</Link>
          <Link to="/pricing" className="hover:text-gray-900">Pricing</Link>
          <Link to="/blog/" className="hover:text-gray-900">Blog</Link>
          <Link to="/docs/overview" className="hover:text-gray-900">Docs</Link>
          <Link to="/privacy-policy" className="hover:text-gray-900">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}
